import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  HiArrowLeft,
  HiCheck,
  HiChevronDown,
  HiChevronRight,
  HiDocumentDuplicate,
  HiPencil,
  HiPlus,
  HiXMark,
} from "react-icons/hi2";

import Header from "../components/layout/Header";
import PlanSessionModal, {
  PlannedSessionInput,
} from "../components/sessions/PlanSessionModal";
import { useAuth } from "../contexts/AuthContext";
import {
  buildProgramWeeks,
  getLocalDateString,
  normalizeProgramStartDate,
  programService,
} from "../services/programService";
import { sessionService } from "../services/sessionService";
import {
  PERIODISATION_OPTIONS,
  Program,
  ProgramWeek,
  Session,
} from "../types/post";
import { TrainingType } from "../types/activity";
import { trainingTypeService } from "../services/trainingTypeService";
import { asDisplayNameOrNull } from "../utils/displayName";
import SearchableSelect from "../components/common/SearchableSelect";

type SavedSession = Session & { id: string };
type ProgramDraft = Omit<Program, "id" | "createdAt" | "updatedAt">;
type ProgramCalendarCell = {
  weekNumber: number;
  dayNumber: number;
  date: string;
};

const WEEKDAY_LABELS = ["M", "T", "W", "T", "F", "S", "S"];
const WEEKDAY_LABELS_FULL = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
const DAY_MS = 1000 * 60 * 60 * 24;

/** Look up the training type name for a session, with fallback to parsing the legacy composite ID. */
const getSessionTypeLabel = (
  session: { trainingTypeID?: string; sessionTypeID?: string },
  trainingTypes?: TrainingType[],
): string | null => {
  const resolved = trainingTypeService.getSessionTrainingTypeName(
    session,
    trainingTypes || [],
  );
  if (resolved) return resolved;
  // Fallback: extract name from legacy composite ID (format: sportId:sessionTypeName)
  const id = session.sessionTypeID?.trim();
  if (!id) return null;
  if (id.includes(":")) {
    return id.split(":")[1] || null;
  }
  return id;
};

const createEmptyProgramDraft = (): ProgramDraft => ({
  name: "",
  description: "",
  startDate: normalizeProgramStartDate(),
  durationWeeks: 8,
  weeks: buildProgramWeeks(8),
  linkedSessions: [],
  isActive: false,
});

const parseLocalDateString = (value: string) => new Date(`${value}T00:00:00`);

const formatShortDateLabel = (value: string) =>
  parseLocalDateString(value).toLocaleDateString("en-AU", {
    day: "numeric",
    month: "short",
  });

const formatLongDateLabel = (value: string) =>
  parseLocalDateString(value).toLocaleDateString("en-AU", {
    weekday: "long",
    day: "numeric",
    month: "long",
    year: "numeric",
  });

const getProgramCalendarCells = (
  startDate: string,
  durationWeeks: number,
): ProgramCalendarCell[] => {
  const start = parseLocalDateString(startDate);
  return Array.from({ length: durationWeeks * 7 }, (_, index) => {
    const date = new Date(start);
    date.setDate(start.getDate() + index);
    return {
      weekNumber: Math.floor(index / 7) + 1,
      dayNumber: (index % 7) + 1,
      date: getLocalDateString(date),
    };
  });
};

const getProgramPlacementForDate = (startDate: string, plannedDate: string) => {
  const start = parseLocalDateString(startDate);
  const target = parseLocalDateString(plannedDate);
  start.setHours(0, 0, 0, 0);
  target.setHours(0, 0, 0, 0);
  const dayOffset = Math.round((target.getTime() - start.getTime()) / DAY_MS);
  if (dayOffset < 0) return null;
  return {
    weekNumber: Math.floor(dayOffset / 7) + 1,
    dayNumber: (dayOffset % 7) + 1,
  };
};

const NewProgram: React.FC = () => {
  const navigate = useNavigate();
  const { programId } = useParams<{ programId?: string }>();
  const { currentUser, userData } = useAuth();
  const isEditing = Boolean(programId);

  const [showSetupDialog, setShowSetupDialog] = useState(!isEditing);
  const [showEditDetailsDialog, setShowEditDetailsDialog] = useState(false);
  const [draft, setDraft] = useState<ProgramDraft>(createEmptyProgramDraft);
  const [scheduledSessions, setScheduledSessions] = useState<SavedSession[]>(
    [],
  );
  const [selectedDay, setSelectedDay] = useState<ProgramCalendarCell | null>(
    null,
  );
  const [isPlanSessionModalOpen, setIsPlanSessionModalOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [statusMessage, setStatusMessage] = useState<string | null>(null);
  const [trainingTypes, setTrainingTypes] = useState<TrainingType[]>([]);
  const [existingProgramId, setExistingProgramId] = useState<string | null>(
    programId || null,
  );

  const [collapsedWeeks, setCollapsedWeeks] = useState<Set<number>>(new Set());
  const [removingSessionId, setRemovingSessionId] = useState<string | null>(
    null,
  );
  const [duplicatingSessionId, setDuplicatingSessionId] = useState<
    string | null
  >(null);
  const [draggingSessionId, setDraggingSessionId] = useState<string | null>(
    null,
  );
  const [dragOverDate, setDragOverDate] = useState<string | null>(null);

  const toggleWeekCollapse = (weekIndex: number) => {
    setCollapsedWeeks((prev) => {
      const next = new Set(prev);
      if (next.has(weekIndex)) {
        next.delete(weekIndex);
      } else {
        next.add(weekIndex);
      }
      return next;
    });
  };

  // Computed values
  const calendarCells = useMemo(
    () => getProgramCalendarCells(draft.startDate, draft.durationWeeks),
    [draft.durationWeeks, draft.startDate],
  );
  const calendarRows = useMemo(
    () =>
      Array.from({ length: draft.durationWeeks }, (_, index) =>
        calendarCells.slice(index * 7, index * 7 + 7),
      ),
    [calendarCells, draft.durationWeeks],
  );
  const allowedProgramDates = useMemo(
    () => calendarCells.map((cell) => cell.date),
    [calendarCells],
  );
  const programEndDate = allowedProgramDates[allowedProgramDates.length - 1];

  const programLinkedSessions = useMemo(() => {
    if (!existingProgramId) return [];
    return scheduledSessions
      .filter(
        (session) => session.programLinkage?.programId === existingProgramId,
      )
      .sort((a, b) => a.plannedDate.localeCompare(b.plannedDate));
  }, [scheduledSessions, existingProgramId]);

  const programSessionsByDate = useMemo(() => {
    return programLinkedSessions.reduce<Record<string, SavedSession[]>>(
      (acc, session) => {
        if (!acc[session.plannedDate]) acc[session.plannedDate] = [];
        acc[session.plannedDate].push(session);
        return acc;
      },
      {},
    );
  }, [programLinkedSessions]);

  const selectedDaySessions = useMemo(
    () => (selectedDay ? programSessionsByDate[selectedDay.date] || [] : []),
    [programSessionsByDate, selectedDay],
  );

  // Load existing program data if editing
  useEffect(() => {
    const loadProgram = async () => {
      if (!currentUser?.uid || !programId) return;
      try {
        setLoading(true);
        const [program, sessions] = await Promise.all([
          programService.getProgram(currentUser.uid, programId),
          sessionService.getAllSessions(currentUser.uid),
        ]);
        if (program) {
          setDraft({
            name: program.name,
            description: program.description,
            startDate: program.startDate,
            durationWeeks: program.durationWeeks,
            weeks: buildProgramWeeks(program.durationWeeks, program.weeks),
            linkedSessions: program.linkedSessions,
            isActive: program.isActive ?? false,
          });
          setExistingProgramId(program.id);
        }
        setScheduledSessions(sessions);
      } catch (err) {
        console.error("Error loading program:", err);
        setError("Failed to load program");
      } finally {
        setLoading(false);
      }
    };
    loadProgram();
  }, [currentUser?.uid, programId]);

  // Load training types for display names
  useEffect(() => {
    const fetchTrainingTypes = async () => {
      try {
        const types = await trainingTypeService.fetchTrainingTypes();
        setTrainingTypes(types);
      } catch (err) {
        console.error("Error fetching training types:", err);
      }
    };
    fetchTrainingTypes();
  }, []);

  const handleDurationChange = (durationWeeks: number) => {
    setDraft((cur) => ({
      ...cur,
      durationWeeks,
      weeks: buildProgramWeeks(durationWeeks, cur.weeks),
    }));
  };

  const handleWeekUpdate = (weekNumber: number, value: string) => {
    setDraft((cur) => ({
      ...cur,
      weeks: cur.weeks.map((week) =>
        week.weekNumber === weekNumber
          ? ({
              weekNumber,
              ...(value
                ? { periodisation: value as ProgramWeek["periodisation"] }
                : {}),
            } as ProgramWeek)
          : week,
      ),
    }));
  };

  const handleSave = async () => {
    if (!currentUser?.uid) return;
    try {
      setSaving(true);
      setError(null);
      const payload: ProgramDraft = {
        name: draft.name.trim(),
        description: draft.description.trim(),
        startDate: normalizeProgramStartDate(
          draft.startDate,
          draft.linkedSessions,
        ),
        durationWeeks: draft.durationWeeks,
        weeks: buildProgramWeeks(draft.durationWeeks, draft.weeks),
        linkedSessions: draft.linkedSessions,
        isActive: draft.isActive ?? false,
      };
      if (!payload.name) {
        setError("Program name is required");
        return;
      }
      if (existingProgramId) {
        await programService.updateProgram(
          currentUser.uid,
          existingProgramId,
          payload,
        );
        setStatusMessage("Program updated");
      } else {
        const newId = await programService.createProgram(
          currentUser.uid,
          payload,
        );
        setExistingProgramId(newId);
        setStatusMessage("Program created");
        // Reload sessions to get the linked sessions
        const sessions = await sessionService.getAllSessions(currentUser.uid);
        setScheduledSessions(sessions);
      }
    } catch (saveError) {
      console.error("Error saving program:", saveError);
      setError("Failed to save program");
    } finally {
      setSaving(false);
    }
  };

  const handleCreateAndContinue = async () => {
    await handleSave();
    if (!error) {
      setShowSetupDialog(false);
    }
  };

  const handleToggleActive = async () => {
    if (!currentUser?.uid || !existingProgramId) return;
    const newIsActive = !draft.isActive;
    try {
      setSaving(true);
      setError(null);
      await programService.updateProgram(currentUser.uid, existingProgramId, {
        isActive: newIsActive,
      });
      setDraft((cur) => ({ ...cur, isActive: newIsActive }));
      setStatusMessage(
        newIsActive ? "Program activated" : "Program deactivated",
      );
    } catch (err) {
      console.error("Error toggling program active state:", err);
      setError("Failed to update program status");
    } finally {
      setSaving(false);
    }
  };

  const handleRemoveSession = async (sessionId: string) => {
    if (!currentUser?.uid || !existingProgramId) return;
    try {
      setRemovingSessionId(sessionId);
      await sessionService.deleteSession(currentUser.uid, sessionId);
      const updatedLinked = draft.linkedSessions.filter(
        (ref) => ref.sessionId !== sessionId,
      );
      await programService.updateProgram(currentUser.uid, existingProgramId, {
        linkedSessions: updatedLinked,
      });
      setDraft((cur) => ({ ...cur, linkedSessions: updatedLinked }));
      const sessions = await sessionService.getAllSessions(currentUser.uid);
      setScheduledSessions(sessions);
    } catch (err) {
      console.error("Error removing session:", err);
      setError("Failed to remove session");
    } finally {
      setRemovingSessionId(null);
    }
  };

  const handleDuplicateSession = async (sessionId: string) => {
    if (!currentUser?.uid || !existingProgramId) return;
    const session = programLinkedSessions.find((s) => s.id === sessionId);
    if (!session) return;
    try {
      setDuplicatingSessionId(sessionId);
      const placement = getProgramPlacementForDate(
        draft.startDate,
        session.plannedDate,
      );
      await programService.createProgramLinkedSession(
        currentUser.uid,
        existingProgramId,
        {
          plannedDate: session.plannedDate,
          name: session.name ? `${session.name} (copy)` : "",
          description: session.description || "",
          ActivityMeasurementList: session.ActivityMeasurementList || [],
          trainingTypeID: session.trainingTypeID,
          sessionTypeID: session.sessionTypeID,
          periodisation: session.periodisation,
          status: "planned",
        },
        placement || {},
      );
      const sessions = await sessionService.getAllSessions(currentUser.uid);
      setScheduledSessions(sessions);
      const program = await programService.getProgram(
        currentUser.uid,
        existingProgramId,
      );
      if (program) {
        setDraft((cur) => ({ ...cur, linkedSessions: program.linkedSessions }));
      }
      setStatusMessage("Session duplicated");
    } catch (err) {
      console.error("Error duplicating session:", err);
      setError("Failed to duplicate session");
    } finally {
      setDuplicatingSessionId(null);
    }
  };

  const handleMoveSession = async (sessionId: string, newDate: string) => {
    if (!currentUser?.uid || !existingProgramId) return;
    // Find the session to check it's not already on this date
    const session = programLinkedSessions.find((s) => s.id === sessionId);
    if (!session || session.plannedDate === newDate) return;

    const placement = getProgramPlacementForDate(draft.startDate, newDate);
    if (!placement || placement.weekNumber > draft.durationWeeks) return;

    try {
      // Update the session's plannedDate and programLinkage
      await sessionService.updateSession(currentUser.uid, sessionId, {
        plannedDate: newDate,
        programLinkage: {
          programId: existingProgramId,
          programName: draft.name,
          durationWeeks: draft.durationWeeks,
          weekNumber: placement.weekNumber,
          dayNumber: placement.dayNumber,
        },
      });

      // Update the program's linkedSessions reference
      const updatedLinked = draft.linkedSessions.map((ref) =>
        ref.sessionId === sessionId
          ? { ...ref, plannedDate: newDate, ...placement }
          : ref,
      );
      await programService.updateProgram(currentUser.uid, existingProgramId, {
        linkedSessions: updatedLinked,
      });
      setDraft((cur) => ({ ...cur, linkedSessions: updatedLinked }));

      // Reload sessions
      const sessions = await sessionService.getAllSessions(currentUser.uid);
      setScheduledSessions(sessions);
      setStatusMessage("Session moved");
    } catch (err) {
      console.error("Error moving session:", err);
      setError("Failed to move session");
    }
  };

  const handleOpenDay = (cell: ProgramCalendarCell) => {
    if (!existingProgramId) {
      setError("Save the program first to add sessions.");
      return;
    }
    setError(null);
    setStatusMessage(null);
    setSelectedDay(cell);
  };

  const handleCreateProgramSessions = async (
    sessionsToCreate: PlannedSessionInput[],
  ) => {
    if (!currentUser?.uid || !existingProgramId) {
      throw new Error("Save the program before assigning sessions.");
    }
    const createdSessionIds: string[] = [];
    for (const sessionData of sessionsToCreate) {
      const placement = getProgramPlacementForDate(
        draft.startDate,
        sessionData.plannedDate,
      );
      if (!placement || placement.weekNumber > draft.durationWeeks) {
        throw new Error(
          "Selected date is outside the current program calendar.",
        );
      }
      const sessionId = await programService.createProgramLinkedSession(
        currentUser.uid,
        existingProgramId,
        sessionData,
        placement,
      );
      createdSessionIds.push(sessionId);
    }
    // Reload sessions
    const sessions = await sessionService.getAllSessions(currentUser.uid);
    setScheduledSessions(sessions);
    // Update draft linked sessions
    const program = await programService.getProgram(
      currentUser.uid,
      existingProgramId,
    );
    if (program) {
      setDraft((cur) => ({ ...cur, linkedSessions: program.linkedSessions }));
    }
    setStatusMessage(
      createdSessionIds.length === 1
        ? "Session added"
        : `${createdSessionIds.length} sessions added`,
    );
    return createdSessionIds;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-base-200">
        <Header />
        <main className="max-w-3xl mx-auto px-8 pt-24 pb-28">
          <div className="animate-pulse space-y-4">
            <div className="h-8 bg-base-300 rounded w-1/3" />
            <div className="h-4 bg-base-300 rounded w-1/2" />
            <div className="h-64 bg-base-300 rounded" />
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-base-200">
      <Header />
      <main className="max-w-3xl mx-auto px-8 pt-24 pb-28">
        {/* Back button and title */}
        <div className="mb-6 flex justify-between items-center">
          <button
            onClick={() => navigate("/programs")}
            className="flex items-center gap-1 text-sm text-base-content/60 hover:text-base-content transition-colors font-grotesk "
          >
            <HiArrowLeft className="text-base" />
            Back to Programs
          </button>
          {/* <h1 className="text-3xl font-bold text-base-content font-heading">
            {isEditing ? "Edit Program" : "New Program"}
          </h1> */}
          <div>
            {/* Error / Status messages */}
            {error && (
              <div
                className="rounded-lg border border-red-200 bg-red-500/10 px-4 py-3 text-sm text-red-700 dark:text-red-400"
                role="alert"
              >
                {error}
              </div>
            )}
            {statusMessage && (
              <div
                className=" bg-green-500/10 rounded-lg px-2 py-1.5 text-sm text-green-700 dark:text-green-400"
                role="status"
              >
                {statusMessage}
              </div>
            )}
          </div>
        </div>

        {/* Program Details (inline, editable at top) */}
        <section className="space-y-6">
          <div className="">
            {/* Program details summary */}
            <div className="flex items-start justify-between gap-4 mb-4">
              <div className="flex-1 min-w-0">
                <h2 className="text-2xl font-semibold text-base-content font-heading truncate">
                  {draft.name || "Untitled Program"}
                </h2>
                <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-1">
                  <span className="text-sm text-base-content/60 font-grotesk">
                    {formatShortDateLabel(draft.startDate)} –{" "}
                    {draft.durationWeeks} week
                    {draft.durationWeeks === 1 ? "" : "s"}
                  </span>
                  {draft.description && (
                    <span className="text-sm text-base-content/50 font-grotesk truncate max-w-xs">
                      {draft.description}
                    </span>
                  )}
                  <span className="text-sm text-base-content/60 font-grotesk whitespace-nowrap">
                    {programLinkedSessions.length} session
                    {programLinkedSessions.length === 1 ? "" : "s"}
                  </span>
                </div>
              </div>
              <button
                type="button"
                onClick={() => setShowEditDetailsDialog(true)}
                className="flex-shrink-0 rounded-lg p-2 text-base-content/50 hover:bg-base-200 hover:text-base-content transition-colors"
                aria-label="Edit program details"
              >
                <HiPencil className="text-lg" />
              </button>
            </div>

            {/* Activate / Deactivate toggle */}
            {existingProgramId && (
              <div className="mb-4 flex items-center justify-between rounded-lg border border-base-300 bg-base-100 px-4 py-3">
                <div>
                  <span className="text-sm font-medium text-base-content font-grotesk">
                    {draft.isActive ? "Active" : "Inactive"}
                  </span>
                  <p className="text-xs text-base-content/50 font-grotesk mt-0.5">
                    {draft.isActive
                      ? "Sessions from this program appear on the calendar"
                      : "Activate to add sessions to the calendar"}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={handleToggleActive}
                  disabled={saving}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus-visible:ring-2 focus-visible:ring-offset-2 disabled:opacity-50 ${
                    draft.isActive ? "bg-green-500" : "bg-base-content/20"
                  }`}
                  role="switch"
                  aria-checked={draft.isActive ?? false}
                  aria-label="Toggle program active"
                >
                  <span
                    className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${
                      draft.isActive ? "translate-x-6" : "translate-x-1"
                    }`}
                  />
                </button>
              </div>
            )}

            {!existingProgramId && (
              <div className="mb-4 rounded-lg border border-yellow-200 bg-yellow-500/10 px-4 py-3 text-sm text-yellow-700 dark:text-yellow-400">
                Go back and save the program first to enable session assignment.
              </div>
            )}

            <div className="space-y-4">
              {calendarRows.map((weekCells, weekIndex) => {
                const week = draft.weeks[weekIndex];
                const weekStartDate = weekCells[0]?.date || "";
                const weekEndDate = weekCells[6]?.date || "";

                const isCollapsed = collapsedWeeks.has(weekIndex);
                const weekSessionCount = weekCells.reduce(
                  (count, cell) =>
                    count + (programSessionsByDate[cell.date]?.length || 0),
                  0,
                );

                return (
                  <div
                    key={`week-${weekIndex + 1}`}
                    className="bg-base-100 rounded-md border border-base-300 shadow-sm p-4"
                  >
                    {/* Week header — clickable to collapse */}
                    <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                      <button
                        type="button"
                        onClick={() => toggleWeekCollapse(weekIndex)}
                        className="flex items-center gap-2 text-left group"
                      >
                        {isCollapsed ? (
                          <HiChevronRight className="text-base text-base-content/40 group-hover:text-base-content/70 transition-colors" />
                        ) : (
                          <HiChevronDown className="text-base text-base-content/40 group-hover:text-base-content/70 transition-colors" />
                        )}
                        <div>
                          <h3 className="text-base font-semibold text-base-content font-heading">
                            Week {weekIndex + 1}
                            {isCollapsed && weekSessionCount > 0 && (
                              <span className="ml-2 text-xs font-normal text-base-content/50">
                                ({weekSessionCount} session
                                {weekSessionCount === 1 ? "" : "s"})
                              </span>
                            )}
                          </h3>
                          <p className="text-xs text-base-content/60 font-grotesk">
                            {formatShortDateLabel(weekStartDate)} –{" "}
                            {formatShortDateLabel(weekEndDate)}
                          </p>
                        </div>
                      </button>
                      {!isCollapsed && (
                        <div className="min-w-[160px]">
                          <SearchableSelect
                            options={[
                              ...PERIODISATION_OPTIONS.map((option) => ({
                                value: option.value,
                                label: option.label,
                              })),
                              ...(userData?.customPeriodisationTypes || []).map(
                                (t) => ({
                                  value: t,
                                  label: t,
                                }),
                              ),
                            ].sort((a, b) => a.label.localeCompare(b.label))}
                            value={week?.periodisation || "all"}
                            onChange={(value) =>
                              handleWeekUpdate(
                                weekIndex + 1,
                                value === "all" ? "" : value,
                              )
                            }
                            placeholder="Search..."
                            allOptionLabel="None"
                            emptyValue="all"
                            inputClassName="bg-base-200"
                          />
                        </div>
                      )}
                    </div>

                    {!isCollapsed && (
                      <div className="flex flex-col">
                        {weekCells.map((cell, dayIdx) => {
                          const sessionsForDate =
                            programSessionsByDate[cell.date] || [];
                          const hasSessions = sessionsForDate.length > 0;

                          return (
                            <div
                              key={cell.date}
                              className={`flex items-start gap-3 border-b last:border-b-0 px-3 py-3 text-left transition-colors ${
                                dragOverDate === cell.date
                                  ? "bg-primary/10 border-primary/30"
                                  : "bg-base-100 border-base-300"
                              } ${!existingProgramId ? "bg-base-100/60 border-base-300/60 cursor-not-allowed" : ""}`}
                              onDragOver={(e) => {
                                if (!existingProgramId) return;
                                e.preventDefault();
                                e.dataTransfer.dropEffect = "move";
                                setDragOverDate(cell.date);
                              }}
                              onDragLeave={() => setDragOverDate(null)}
                              onDrop={(e) => {
                                e.preventDefault();
                                setDragOverDate(null);
                                const sessionId =
                                  e.dataTransfer.getData("text/plain");
                                if (sessionId) {
                                  handleMoveSession(sessionId, cell.date);
                                }
                              }}
                              onClick={() =>
                                existingProgramId && handleOpenDay(cell)
                              }
                              role={existingProgramId ? "button" : undefined}
                              tabIndex={existingProgramId ? 0 : undefined}
                              aria-label={`${WEEKDAY_LABELS_FULL[cell.dayNumber - 1]} ${formatShortDateLabel(cell.date)}, ${sessionsForDate.length} session${sessionsForDate.length === 1 ? "" : "s"}`}
                            >
                              <div className="w-16 flex-shrink-0 pt-0.5">
                                <div className="text-xs font-semibold uppercase tracking-wide text-base-content/50">
                                  {WEEKDAY_LABELS[dayIdx]}
                                </div>
                                <div className="text-[10px] font-medium text-base-content/40 font-grotesk mt-0.5">
                                  {formatShortDateLabel(cell.date)}
                                </div>
                              </div>
                              <div className="flex-1 min-w-0">
                                {hasSessions ? (
                                  <div className="flex flex-col gap-1">
                                    {sessionsForDate
                                      .slice(0, 3)
                                      .map((session) => (
                                        <div
                                          key={session.id}
                                          draggable={!!existingProgramId}
                                          onDragStart={(e) => {
                                            e.dataTransfer.setData(
                                              "text/plain",
                                              session.id,
                                            );
                                            e.dataTransfer.effectAllowed =
                                              "move";
                                            setDraggingSessionId(session.id);
                                            e.stopPropagation();
                                          }}
                                          onDragEnd={() =>
                                            setDraggingSessionId(null)
                                          }
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            if (draggingSessionId) return;
                                            navigate(`/session/${session.id}`);
                                          }}
                                          className={`rounded-md bg-neutral/15 dark:bg-base-200 border border-neutral/20 dark:border-neutral/60 p-4 ${
                                            existingProgramId
                                              ? "cursor-grab active:cursor-grabbing"
                                              : "cursor-pointer"
                                          } ${draggingSessionId === session.id ? "opacity-40" : ""}`}
                                        >
                                          <div className="flex items-center justify-between gap-1">
                                            <span className="text-[10px] text-sm font-medium text-base-content dark:text-base-content/90 font-grotesk line-clamp-1 flex-1 min-w-0">
                                              {asDisplayNameOrNull(
                                                session.name,
                                              ) ||
                                                getSessionTypeLabel(
                                                  session,
                                                  trainingTypes,
                                                ) ||
                                                "Session"}
                                            </span>
                                            <div className="flex items-center gap-0.5 flex-shrink-0">
                                              <button
                                                type="button"
                                                onClick={(e) => {
                                                  e.stopPropagation();
                                                  handleDuplicateSession(
                                                    session.id,
                                                  );
                                                }}
                                                disabled={
                                                  duplicatingSessionId ===
                                                  session.id
                                                }
                                                className="p-0.5 rounded text-base-content/30 hover:text-base-content/70 hover:bg-base-content/10 transition-colors disabled:opacity-50"
                                                aria-label="Duplicate session"
                                              >
                                                <HiDocumentDuplicate className="w-3 h-3" />
                                              </button>
                                              <button
                                                type="button"
                                                onClick={(e) => {
                                                  e.stopPropagation();
                                                  handleRemoveSession(
                                                    session.id,
                                                  );
                                                }}
                                                disabled={
                                                  removingSessionId ===
                                                  session.id
                                                }
                                                className="p-0.5 rounded text-base-content/30 hover:text-red-500 hover:bg-red-500/10 transition-colors disabled:opacity-50"
                                                aria-label="Delete session"
                                              >
                                                <HiXMark className="w-3 h-3" />
                                              </button>
                                            </div>
                                          </div>
                                        </div>
                                      ))}
                                    {sessionsForDate.length > 3 && (
                                      <span className="text-[10px] text-base-content/50 font-grotesk">
                                        +{sessionsForDate.length - 3} more
                                      </span>
                                    )}
                                  </div>
                                ) : existingProgramId ? (
                                  <span className="text-[10px] text-base-content/30 font-grotesk">
                                    + Add
                                  </span>
                                ) : null}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          {/* Save & Done */}
          <div className="flex justify-end gap-3 pt-2">
            <button
              onClick={handleSave}
              disabled={saving || !draft.name.trim()}
              className="inline-flex items-center gap-2 px-6 py-3 text-sm font-medium text-white bg-neutral rounded-lg hover:bg-neutral/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed font-grotesk"
            >
              {saving ? "Saving..." : "Save"}
              <HiCheck className="text-base" />
            </button>
          </div>
        </section>
      </main>

      {/* Day management modal */}
      {selectedDay && (
        <div
          className="fixed inset-0 z-[60] bg-black/55 backdrop-blur-sm p-4 flex items-center justify-center"
          onClick={() => setSelectedDay(null)}
        >
          <div
            className="w-full max-w-2xl rounded-2xl bg-base-100 shadow-xl border border-base-300 p-6 space-y-5"
            onClick={(event) => event.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-label={`Manage sessions for ${formatLongDateLabel(selectedDay.date)}`}
          >
            <div className="flex items-start justify-between gap-4">
              <div>
                <h3 className="text-xl font-semibold text-base-content font-heading">
                  {formatLongDateLabel(selectedDay.date)}
                </h3>
                <p className="mt-1 text-sm text-base-content/60 font-grotesk">
                  Week {selectedDay.weekNumber} • Day {selectedDay.dayNumber}
                  {draft.weeks[selectedDay.weekNumber - 1]?.periodisation
                    ? ` • ${draft.weeks[selectedDay.weekNumber - 1]?.periodisation}`
                    : ""}
                </p>
              </div>
              <button
                type="button"
                onClick={() => setSelectedDay(null)}
                className="rounded-full p-2 text-base-content/60 hover:bg-base-200 hover:text-base-content transition-colors"
                aria-label="Close day manager"
              >
                <HiXMark className="text-xl" />
              </button>
            </div>

            <div>
              {selectedDaySessions.length === 0 ? (
                <p className="mt-3 text-sm text-base-content/60 font-grotesk">
                  No sessions assigned yet. Create one from scratch or choose a
                  session template.
                </p>
              ) : (
                <div className="mt-3 space-y-2">
                  {selectedDaySessions.map((session) => (
                    <div
                      key={session.id}
                      className="rounded-xl border border-base-300 bg-base-200 dark:bg-base-200/60 px-4 py- hover:bg-base-300 dark:hover:bg-base-200 transition-colors"
                    >
                      <div className="flex items-start justify-between gap-2">
                        <button
                          type="button"
                          onClick={() => navigate(`/session/${session.id}`)}
                          className="flex-1 min-w-0 text-left focus-visible:ring-2 focus-visible:ring-neutral rounded"
                        >
                          <div className="text-sm font-medium text-base-content font-grotesk">
                            {asDisplayNameOrNull(session.name) ||
                              getSessionTypeLabel(session, trainingTypes) ||
                              "Untitled session"}
                          </div>
                          <div className="mt-1 text-xs text-base-content/60 font-grotesk">
                            {session.ActivityMeasurementList?.length || 0}{" "}
                            activit
                            {session.ActivityMeasurementList?.length === 1
                              ? "y"
                              : "ies"}
                            {session.trainingTypeID || session.sessionTypeID
                              ? ` • ${getSessionTypeLabel(session, trainingTypes) || ""}`
                              : ""}
                          </div>
                        </button>
                        <div className="flex items-center gap-4 flex-shrink-0">
                          <button
                            type="button"
                            onClick={() => handleDuplicateSession(session.id)}
                            disabled={duplicatingSessionId === session.id}
                            className="rounded-full p-1 text-base-content/40 hover:bg-base-content/10 hover:text-base-content/70 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                            aria-label={`Duplicate session ${asDisplayNameOrNull(session.name) || "Untitled"}`}
                          >
                            {duplicatingSessionId === session.id ? (
                              <span className="loading loading-spinner loading-xs" />
                            ) : (
                              <HiDocumentDuplicate className="text-lg" />
                            )}
                          </button>
                          <button
                            type="button"
                            onClick={() => handleRemoveSession(session.id)}
                            disabled={removingSessionId === session.id}
                            className="rounded-full p-1 text-base-content/40 hover:bg-red-500/10 hover:text-red-500 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                            aria-label={`Remove session ${asDisplayNameOrNull(session.name) || "Untitled"}`}
                          >
                            {removingSessionId === session.id ? (
                              <span className="loading loading-spinner loading-xs" />
                            ) : (
                              <HiXMark className="text-lg" />
                            )}
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="flex justify-end">
              <button
                type="button"
                onClick={() => setIsPlanSessionModalOpen(true)}
                className="inline-flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-medium text-white bg-neutral rounded-lg hover:bg-neutral/90 transition-colors font-grotesk"
              >
                <HiPlus className="text-base" />
                Add session
              </button>
            </div>
          </div>
        </div>
      )}

      <PlanSessionModal
        isOpen={isPlanSessionModalOpen}
        onClose={() => setIsPlanSessionModalOpen(false)}
        initialPlannedDate={selectedDay?.date ?? null}
        saveSessions={handleCreateProgramSessions}
        navigateOnSave={false}
        minPlannedDate={draft.startDate}
        maxPlannedDate={programEndDate}
        allowedPlannedDates={allowedProgramDates}
      />

      {/* Setup dialog for new programs */}
      {showSetupDialog && (
        <div className="fixed inset-0 z-[70] bg-black/55 backdrop-blur-sm p-4 flex items-center justify-center">
          <div
            className="w-full max-w-lg rounded-2xl bg-base-100 shadow-xl border border-base-300 p-6 space-y-5"
            role="dialog"
            aria-modal="true"
            aria-label="Set up new program"
          >
            <div>
              <h2 className="text-xl font-semibold text-base-content font-heading">
                New Program
              </h2>
              <p className="mt-1 text-sm text-base-content/60 font-grotesk">
                Set up the basics for your training program.
              </p>
            </div>

            <div className="space-y-4">
              <label className="form-control">
                <span className="label-text text-sm font-medium text-base-content mb-2">
                  Program name
                </span>
                <input
                  aria-label="Program name"
                  type="text"
                  value={draft.name}
                  onChange={(e) =>
                    setDraft((cur) => ({ ...cur, name: e.target.value }))
                  }
                  className="input bg-base-200 text-[16px] sm:text-sm w-full focus:outline-none focus:ring-1 focus:ring-base-content/80 focus:border-base-content/80 border-base-200 border-2 p-3 rounded-md font-sans"
                  placeholder="e.g. 8 Week 5km Plan"
                  autoFocus
                />
              </label>

              <div className="grid gap-4 sm:grid-cols-2">
                <label className="form-control">
                  <span className="label-text text-sm font-medium text-base-content mb-2">
                    Start date
                  </span>
                  <input
                    aria-label="Start date"
                    type="date"
                    value={draft.startDate}
                    onChange={(e) =>
                      setDraft((cur) => ({
                        ...cur,
                        startDate: normalizeProgramStartDate(
                          e.target.value,
                          cur.linkedSessions,
                        ),
                      }))
                    }
                    className="input bg-base-200 text-[16px] sm:text-sm w-full focus:outline-none focus:ring-1 focus:ring-base-content/80 focus:border-base-content/80 border-base-200 border-2 p-3 rounded-md font-sans cursor-pointer"
                  />
                  <span className="label-text-alt text-xs text-base-content/60 mt-1">
                    Weeks run Monday–Sunday. Aligned to the start of the
                    selected week.
                  </span>
                </label>

                <div className="form-control">
                  <SearchableSelect
                    options={Array.from({ length: 12 }, (_, i) => i + 1).map(
                      (weeks) => ({
                        value: String(weeks),
                        label: `${weeks} week${weeks === 1 ? "" : "s"}`,
                      }),
                    )}
                    value={String(draft.durationWeeks)}
                    onChange={(value) => handleDurationChange(Number(value))}
                    placeholder="Search..."
                    allOptionLabel="1 week"
                    emptyValue=""
                    label="Duration"
                    inputClassName="bg-base-200"
                  />
                </div>
              </div>

              <label className="form-control">
                <span className="label-text text-sm font-medium text-base-content mb-2">
                  Description
                </span>
                <textarea
                  aria-label="Description"
                  value={draft.description}
                  onChange={(e) =>
                    setDraft((cur) => ({ ...cur, description: e.target.value }))
                  }
                  className="textarea bg-base-200 min-h-[80px] w-full focus:outline-none focus:ring-1 focus:ring-base-content/80 focus:border-base-content/80 border-base-200 border-2 p-3 rounded-md font-sans text-[16px] sm:text-sm"
                  placeholder="Outline the goal, race, or block this program supports."
                />
              </label>
            </div>

            <div className="flex justify-end gap-3 pt-2">
              <button
                onClick={() => navigate("/programs")}
                className="px-5 py-2.5 text-sm font-medium text-base-content/70 bg-base-100 border border-base-300 rounded-lg hover:bg-base-200 transition-colors font-grotesk"
              >
                Cancel
              </button>
              <button
                onClick={handleCreateAndContinue}
                disabled={!draft.name.trim() || saving}
                className="inline-flex items-center gap-2 px-5 py-2.5 text-sm font-medium text-white bg-neutral rounded-lg hover:bg-neutral/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed font-grotesk"
              >
                {saving ? "Creating..." : "Create Program"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit details dialog */}
      {showEditDetailsDialog && (
        <div
          className="fixed inset-0 z-[70] bg-black/55 backdrop-blur-sm p-4 flex items-center justify-center"
          onClick={() => setShowEditDetailsDialog(false)}
        >
          <div
            className="w-full max-w-lg rounded-2xl bg-base-100 shadow-xl border border-base-300 p-6 space-y-5"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-label="Edit program details"
          >
            <div className="flex items-start justify-between gap-4">
              <div>
                <h2 className="text-xl font-semibold text-base-content font-heading">
                  Edit Program Details
                </h2>
                <p className="mt-1 text-sm text-base-content/60 font-grotesk">
                  Update the basics for your training program.
                </p>
              </div>
              <button
                type="button"
                onClick={() => setShowEditDetailsDialog(false)}
                className="rounded-full p-2 text-base-content/60 hover:bg-base-200 hover:text-base-content transition-colors"
                aria-label="Close"
              >
                <HiXMark className="text-xl" />
              </button>
            </div>

            <div className="space-y-4">
              <label className="form-control">
                <span className="label-text text-sm font-medium text-base-content mb-2">
                  Program name
                </span>
                <input
                  aria-label="Program name"
                  type="text"
                  value={draft.name}
                  onChange={(e) =>
                    setDraft((cur) => ({ ...cur, name: e.target.value }))
                  }
                  className="input bg-base-200 text-[16px] sm:text-sm w-full focus:outline-none focus:ring-1 focus:ring-base-content/80 focus:border-base-content/80 border-base-200 border-2 p-3 rounded-md font-sans"
                  placeholder="e.g. 8 Week 5km Plan"
                  autoFocus
                />
              </label>

              <div className="grid gap-4 sm:grid-cols-2">
                <label className="form-control">
                  <span className="label-text text-sm font-medium text-base-content mb-2">
                    Start date
                  </span>
                  <input
                    aria-label="Start date"
                    type="date"
                    value={draft.startDate}
                    onChange={(e) =>
                      setDraft((cur) => ({
                        ...cur,
                        startDate: normalizeProgramStartDate(
                          e.target.value,
                          cur.linkedSessions,
                        ),
                      }))
                    }
                    className="input bg-base-200 text-[16px] sm:text-sm w-full focus:outline-none focus:ring-1 focus:ring-base-content/80 focus:border-base-content/80 border-base-200 border-2 p-3 rounded-md font-sans cursor-pointer"
                  />
                  <span className="label-text-alt text-xs text-base-content/60 mt-1">
                    Weeks run Monday–Sunday. Aligned to the start of the
                    selected week.
                  </span>
                </label>

                <div className="form-control">
                  <SearchableSelect
                    options={Array.from({ length: 12 }, (_, i) => i + 1).map(
                      (weeks) => ({
                        value: String(weeks),
                        label: `${weeks} week${weeks === 1 ? "" : "s"}`,
                      }),
                    )}
                    value={String(draft.durationWeeks)}
                    onChange={(value) => handleDurationChange(Number(value))}
                    placeholder="Search..."
                    allOptionLabel="1 week"
                    emptyValue=""
                    label="Duration"
                    inputClassName="bg-base-200"
                  />
                </div>
              </div>

              <label className="form-control">
                <span className="label-text text-sm font-medium text-base-content mb-2">
                  Description
                </span>
                <textarea
                  aria-label="Description"
                  value={draft.description}
                  onChange={(e) =>
                    setDraft((cur) => ({ ...cur, description: e.target.value }))
                  }
                  className="textarea bg-base-200 min-h-[80px] w-full focus:outline-none focus:ring-1 focus:ring-base-content/80 focus:border-base-content/80 border-base-200 border-2 p-3 rounded-md font-sans text-[16px] sm:text-sm"
                  placeholder="Outline the goal, race, or block this program supports."
                />
              </label>
            </div>

            <div className="flex justify-end pt-2">
              <button
                onClick={() => setShowEditDetailsDialog(false)}
                className="inline-flex items-center gap-2 px-5 py-2.5 text-sm font-medium text-white bg-neutral rounded-lg hover:bg-neutral/90 transition-colors font-grotesk"
              >
                Done
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default NewProgram;
